import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Subject, of, catchError, debounceTime, switchMap, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DataManipulationService } from 'src/app/services/data-manipulation.service';
import { ApiwrapperService } from 'src/app/services/apiwrapper.service';

export interface ScheduledPause {
  uuid?: string;
  startDate: string;
  endDate: string;
  nextSendDate?: string;
  status?: string;
}

export function extractScheduledPause(poll: any): ScheduledPause | null {
  if (!poll) {
    return null;
  }
  const nestedRaw = poll.pausedPolling
    || poll.pause
    || poll._embedded?.pausedPolling
    || poll._embedded?.['paused-polling'];
  const nested = Array.isArray(nestedRaw) ? nestedRaw[0] : nestedRaw;
  if (!nested) {
    return null;
  }
  const status = nested?.status || poll.pausedPollingStatus;
  if (status === 'canceled') {
    return null;
  }
  const start = normalizePauseDate(nested?.startDate || nested?.pauseStartDate || poll.pauseStartDate || poll.pausedStartDate);
  const end = normalizePauseDate(nested?.endDate || nested?.pauseEndDate || poll.pauseEndDate || poll.pausedEndDate);
  if (!start || !end) {
    return null;
  }
  const today = toApiDate(new Date());
  if (end < today) {
    return null;
  }
  return {
    uuid: nested?.uuid || nested?.pausedPollingUuid || poll.pausedPollingUuid,
    startDate: start,
    endDate: end,
    nextSendDate: normalizePauseDate(nested?.nextPoll?.date || nested?.nextPoll || nested?.nextSendDate || poll.nextPoll?.date || poll.pauseNextSendDate),
    status
  };
}

function normalizePauseDate(value: any): string {
  if (!value) {
    return '';
  }
  if (typeof value === 'string') {
    return value.substring(0, 10);
  }
  if (value.date) {
    return String(value.date).substring(0, 10);
  }
  return '';
}

function toApiDate(date: Date): string {
  const month = ('0' + (date.getMonth() + 1)).slice(-2);
  const day = ('0' + date.getDate()).slice(-2);
  return `${date.getFullYear()}-${month}-${day}`;
}

@Component({
  selector: 'app-pause-loop-modal',
  templateUrl: './pause-loop-modal.component.html',
  styleUrls: ['./pause-loop-modal.component.scss']
})
export class PauseLoopModalComponent implements OnInit, OnDestroy {
  @Input() teamUuid = '';
  @Input() pollDetails: any = null;
  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<{ pollDetails: any }>();
  @Output() removed = new EventEmitter<{ pollDetails: any }>();

  startDate = '';
  endDate = '';
  startDateValue: Date | null = null;
  endDateValue: Date | null = null;
  minDate = new Date();
  endMinDate = new Date();
  todayStr = '';
  hasExistingPause = false;
  existingPause: ScheduledPause | null = null;
  dateError = false;
  datesValid = false;
  previewDays = 0;
  noSendsLabel = '';
  nextSendLabel = '';
  existingRangeLabel = '';
  existingNextSendLabel = '';
  pauseStatusChip = 'scheduled';
  saving = false;
  removing = false;
  previewLoading = false;

  private previewTrigger$ = new Subject<{ startDate: string; endDate: string }>();
  private destroyed$ = new Subject<void>();

  constructor(
    private api: ApiwrapperService,
    private datePipe: DatePipe,
    private translate: TranslateService,
    private data: DataManipulationService
  ) {}

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  ngOnInit(): void {
    this.minDate = new Date();
    this.minDate.setHours(0, 0, 0, 0);
    this.todayStr = toApiDate(this.minDate);
    this.updateEndMinDate();
    this.existingPause = extractScheduledPause(this.pollDetails);
    this.hasExistingPause = !!this.existingPause;
    if (this.existingPause) {
      this.startDate = this.existingPause.startDate;
      this.endDate = this.existingPause.endDate;
      this.startDateValue = this.parseDateValue(this.existingPause.startDate);
      this.endDateValue = this.parseDateValue(this.existingPause.endDate);
      this.updateEndMinDate();
      this.existingRangeLabel = this.formatBannerRange(this.existingPause.startDate, this.existingPause.endDate);
      this.existingNextSendLabel = this.formatNextSend(this.existingPause.nextSendDate);
      this.pauseStatusChip = this.getPauseChip(this.existingPause.startDate, this.existingPause.endDate);
    }
    this.previewTrigger$.pipe(
      debounceTime(150),
      switchMap((dates) => {
        const pollingUuid = this.pollDetails?.uuid;
        if (!pollingUuid) {
          return of(null);
        }
        this.previewLoading = true;
        return this.api.getPausedPollingPreview(
          pollingUuid,
          dates.startDate,
          dates.endDate,
          this.teamUuid
        ).pipe(
          catchError(() => of(null))
        );
      }),
      takeUntil(this.destroyed$)
    ).subscribe((res) => {
      this.previewLoading = false;
      if (!this.datesValid) {
        return;
      }
      this.nextSendLabel = this.formatNextSend(this.extractNextSend(res));
      if (this.hasExistingPause && this.nextSendLabel) {
        this.existingNextSendLabel = this.nextSendLabel;
      }
    });
    this.onDatesChange();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  close(event?: Event): void {
    event?.stopPropagation();
    this.closed.emit();
  }

  onOverlayClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  onDatesChange(): void {
    this.updateEndMinDate();
    if (this.endDateValue && this.toDayStart(this.endDateValue).getTime() < this.endMinDate.getTime()) {
      this.endDateValue = null;
    }
    this.startDate = this.startDateValue ? toApiDate(this.startDateValue) : '';
    this.endDate = this.endDateValue ? toApiDate(this.endDateValue) : '';
    this.nextSendLabel = '';
    if (!this.startDate || !this.endDate) {
      this.dateError = false;
      this.datesValid = false;
      this.previewDays = 0;
      this.noSendsLabel = '';
      return;
    }
    if (this.endDate <= this.startDate) {
      this.dateError = true;
      this.datesValid = false;
      this.previewDays = 0;
      this.noSendsLabel = '';
      return;
    }
    this.dateError = false;
    this.datesValid = true;
    this.previewDays = this.countInclusiveDays(this.startDate, this.endDate);
    this.noSendsLabel = this.formatShortRange(this.startDate, this.endDate);
    this.previewTrigger$.next({ startDate: this.startDate, endDate: this.endDate });
  }

  private updateEndMinDate(): void {
    const base = this.startDateValue
      ? this.toDayStart(this.startDateValue)
      : this.toDayStart(this.minDate);
    base.setDate(base.getDate() + 1);
    if (!this.endMinDate || this.endMinDate.getTime() !== base.getTime()) {
      this.endMinDate = base;
    }
  }

  private toDayStart(date: Date): Date {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
  }

  savePause(event: Event): void {
    event.stopPropagation();
    if (!this.datesValid || this.saving || this.removing) {
      return;
    }
    const pollingUuid = this.pollDetails?.uuid;
    if (!pollingUuid) {
      return;
    }
    this.saving = true;
    const request$ = this.hasExistingPause && this.existingPause?.uuid
      ? this.api.updatePausedPolling(this.teamUuid, {
          pausedPollingUuid: this.existingPause.uuid,
          startDate: this.startDate,
          endDate: this.endDate
        })
      : this.api.createPausedPolling(this.teamUuid, {
          pollingUuid,
          startDate: this.startDate,
          endDate: this.endDate
        });
    request$.subscribe((res: any) => {
      this.saving = false;
      if (res?.error || res?.info?.error) {
        this.data.showAlertError(this.getErrorMessage(res, 'to_pause_loop_save_error'));
        return;
      }
      this.data.showAlert(this.getSuccessMessage(res, this.hasExistingPause ? 'to_pause_loop_update_success' : 'to_pause_loop_save_success'));
      this.saved.emit({ pollDetails: this.mergePauseIntoPoll(res) });
    }, () => {
      this.saving = false;
      this.data.showAlertError(this.translate.instant('to_pause_loop_save_error'));
    });
  }

  removePause(event: Event): void {
    event.stopPropagation();
    const pausedPollingUuid = this.existingPause?.uuid;
    if (!pausedPollingUuid || this.saving || this.removing) {
      return;
    }
    this.removing = true;
    this.api.updatePausedPolling(this.teamUuid, {
      pausedPollingUuid,
      status: 'canceled'
    }).subscribe((res: any) => {
      this.removing = false;
      if (res?.error || res?.info?.error) {
        this.data.showAlertError(this.getErrorMessage(res, 'to_pause_loop_remove_error'));
        return;
      }
      this.data.showAlert(this.getSuccessMessage(res, 'to_pause_loop_remove_success'));
      this.removed.emit({ pollDetails: this.clearPauseFromPoll(res) });
    }, () => {
      this.removing = false;
      this.data.showAlertError(this.translate.instant('to_pause_loop_remove_error'));
    });
  }

  get previewTitleKey(): string {
    if (this.dateError) {
      return 'to_pause_loop_fix_dates';
    }
    if (!this.datesValid) {
      return 'to_pause_loop_select_dates_preview';
    }
    return this.previewDays === 1 ? 'to_pause_loop_day_without_send' : 'to_pause_loop_days_without_send';
  }

  private mergePauseIntoPoll(res: any): any {
    const poll = this.pollDetails || {};
    if (res && (res.pollingFrequency || res.cronNextRun || res.status)) {
      return res;
    }
    const pause = extractScheduledPause(res) || {
      uuid: res?.uuid || res?.pausedPollingUuid || this.existingPause?.uuid,
      startDate: this.startDate,
      endDate: this.endDate,
      nextSendDate: this.extractNextSend(res) || this.nextSendLabel
    };
    return {
      ...poll,
      pausedPolling: {
        ...(poll.pausedPolling || {}),
        ...pause,
        uuid: pause.uuid || res?.uuid || res?.pausedPollingUuid,
        startDate: this.startDate,
        endDate: this.endDate
      },
      pausedPollingUuid: pause.uuid || res?.uuid || res?.pausedPollingUuid || poll.pausedPollingUuid
    };
  }

  private clearPauseFromPoll(res: any): any {
    if (res && (res.pollingFrequency || res.cronNextRun || res.status)) {
      return res;
    }
    const poll = { ...(this.pollDetails || {}) };
    delete poll.pausedPolling;
    delete poll.pause;
    delete poll.pauseStartDate;
    delete poll.pauseEndDate;
    delete poll.pausedStartDate;
    delete poll.pausedEndDate;
    return poll;
  }

  private extractNextSend(res: any): string {
    if (!res || res.error || res.info?.error) {
      return '';
    }
    const value = res.nextPoll?.date;
    return typeof value === 'string' ? value : '';
  }

  private formatNextSend(value?: string): string {
    if (!value) {
      return '';
    }
    const parsed = this.parseDateValue(value);
    if (!parsed) {
      return value;
    }
    return this.datePipe.transform(parsed, 'EEEE, MMM d, y') || value;
  }

  private formatShortRange(start: string, end: string): string {
    return `${this.datePipe.transform(this.parseDateValue(start), 'MMM d')} – ${this.datePipe.transform(this.parseDateValue(end), 'MMM d')}`;
  }

  private formatBannerRange(start: string, end: string): string {
    const startDate = this.parseDateValue(start);
    const endDate = this.parseDateValue(end);
    if (!startDate || !endDate) {
      return `${start} – ${end}`;
    }
    const sameYear = startDate.getFullYear() === endDate.getFullYear();
    const startLabel = this.datePipe.transform(startDate, sameYear ? 'MMM d' : 'MMM d, y');
    const endLabel = this.datePipe.transform(endDate, 'MMM d, y');
    return `${startLabel} – ${endLabel}`;
  }

  private getPauseChip(start: string, end: string): string {
    const today = this.todayStr;
    if (start <= today && today <= end) {
      return 'active';
    }
    return 'scheduled';
  }

  private countInclusiveDays(start: string, end: string): number {
    const startDate = this.parseDateValue(start);
    const endDate = this.parseDateValue(end);
    if (!startDate || !endDate) {
      return 0;
    }
    return Math.round((endDate.getTime() - startDate.getTime()) / 86400000) + 1;
  }

  private parseDateValue(value: string): Date | null {
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) {
      const fallback = new Date(value);
      return isNaN(fallback.getTime()) ? null : fallback;
    }
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  private getErrorMessage(res: any, fallbackKey: string): string {
    return res?.error?.error?.info?.messages?.[0]
      || res?.error?.info?.messages?.[0]
      || res?.info?.messages?.[0]
      || this.translate.instant(fallbackKey);
  }

  private getSuccessMessage(res: any, fallbackKey: string): string {
    return res?.info?.messages?.[0]
      || res?.messages?.[0]
      || this.translate.instant(fallbackKey);
  }
}
